import React, { useState } from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import Swal from "sweetalert2"; // SweetAlert2 for beautiful alerts
import { Camera, User, Mail, Phone, Building, MapPin, Calendar, CheckCircle } from "lucide-react";

const ParalegalPersonalInfo = () => {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ["paralegal-profile"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");

            const res = await api.get("/paralegals/profile", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
    });

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
    });

    React.useEffect(() => {
        if (data) {
            setFormData({
                name: data.name || "",
                email: data.email || "",
                phone: data.phone || "",
            });
            setProfilePicture(data.profile_picture || null);
        }
    }, [data]);

    // === Update Profile Mutation ===
    const updateMutation = useMutation({
        mutationFn: async (updatedData) => {
            const token = localStorage.getItem("auth_token");
            const res = await api.put("/paralegals/profile", updatedData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data;
        },
        onSuccess: (response) => {
            queryClient.setQueryData(["paralegal-profile"], (old) => ({
                ...old,
                ...response.data,
            }));
            setIsEditing(false);

            // Success with SweetAlert2
            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Your profile has been updated successfully.",
                confirmButtonText: "OK",
                confirmButtonColor: "#2563eb",
                timer: 3000,
                timerProgressBar: true,
            });
        },
        onError: (err) => {
            // Error with SweetAlert2
            Swal.fire({
                icon: "error",
                title: "Update Failed",
                text: err.response?.data?.message || "Something went wrong while updating your profile. Please try again.",
                confirmButtonText: "Try Again",
                confirmButtonColor: "#dc2626",
            });
        },
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => setProfilePicture(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Confirm save with SweetAlert2
        Swal.fire({
            title: "Are you sure?",
            text: "You are about to save changes to your profile.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Save",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                updateMutation.mutate(formData);
            }
        });
    };

    const handleCancel = () => {
        // Confirm cancel with SweetAlert2
        Swal.fire({
            title: "Cancel Editing?",
            text: "All changes will be discarded.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel",
            cancelButtonText: "Keep Editing",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData({
                    name: data.name,
                    email: data.email,
                    phone: data.phone,
                });
                setProfilePicture(data.profile_picture || null);
                setIsEditing(false);
            }
        });
    };

    // Loading State
    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="ml-3 text-gray-600">Loading profile...</p>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="text-center py-12 bg-red-50 rounded-lg">
                <p className="text-red-600 font-medium">Failed to load profile.</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    <p className="text-gray-600 mt-1">Update your profile and contact details</p>
                </div>
                {!isEditing && (
                    <motion.button
                        onClick={() => setIsEditing(true)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Edit Profile
                    </motion.button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex items-center space-x-6">
                    <div className="relative">
                        <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                            {profilePicture ? (
                                <img
                                    src={profilePicture}
                                    alt="Profile"
                                    className="w-24 h-24 rounded-full object-cover"
                                />
                            ) : (
                                formData.name.charAt(0).toUpperCase()
                            )}
                        </div>
                        {isEditing && (
                            <label className="absolute bottom-0 right-0 bg-white text-blue-600 rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-100 transition-colors">
                                <Camera className="w-5 h-5" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfilePictureChange}
                                    className="hidden"
                                />
                            </label>
                        )}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Profile Picture</h3>
                        <p className="text-sm text-gray-600">
                            {isEditing ? "Click the camera to upload" : "JPG, PNG up to 2MB"}
                        </p>
                    </div>
                </div>

                {/* Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-1" />
                        Full Name
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your full name"
                    />
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email Address
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your email"
                    />
                </div>

                {/* Phone */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone Number
                    </label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="+1 (555) 000-0000"
                    />
                </div>

                {/* Office Info (Read-only) */}
                <div className="bg-gray-50 rounded-lg p-5 space-y-3">
                    <h3 className="font-semibold text-gray-900 flex items-center">
                        <Building className="w-5 h-5 mr-2 text-blue-600" />
                        Law Firm
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                            <span className="text-gray-600">Firm Name:</span>
                            <p className="font-medium">{data.office.name}</p>
                        </div>
                        <div>
                            <span className="text-gray-600">Location:</span>
                            <p className="font-medium flex items-center">
                                <MapPin className="w-4 h-4 mr-1" />
                                {data.office.city}, {data.office.state}
                            </p>
                        </div>
                        <div>
                            <span className="text-gray-600">Supervising Lawyer:</span>
                            <p className="font-medium">{data.supervising_lawyer.name}</p>
                        </div>
                        <div>
                            <span className="text-gray-600">Member Since:</span>
                            <p className="font-medium flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {new Date(data.joined_at).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                {isEditing && (
                    <div className="flex justify-end space-x-4 pt-6 border-t">
                        <motion.button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Cancel
                        </motion.button>
                        <motion.button
                            type="submit"
                            disabled={updateMutation.isPending}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {updateMutation.isPending ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    Save Changes
                                </>
                            )}
                        </motion.button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default ParalegalPersonalInfo;